import React from "react";
import { Button } from "@/components/ui/button";
import { Layers3, Loader2, ShieldCheck } from "lucide-react";

type ChannelCatalogService = {
  id: number;
  name: string;
  description?: string | null;
  contractLabel?: string | null;
  category?: string | null;
  companyContracted?: boolean;
  modules?: Array<unknown>;
};

type ChannelCatalogPanelProps = {
  channelLabel: string;
  channelContextLabel?: string;
  servicesInCatalog: number;
  servicesContracted: number;
  services: ChannelCatalogService[];
  draftServiceIds: number[];
  isLoading: boolean;
  isSaving: boolean;
  saveButtonLabel?: string;
  saveButtonDisabled?: boolean;
  onToggleService: (serviceId: number, checked: boolean) => void;
  onSave: () => void;
};

export default function ChannelCatalogPanel({
  channelLabel,
  channelContextLabel = "Canal em edição",
  servicesInCatalog,
  servicesContracted,
  services,
  draftServiceIds,
  isLoading,
  isSaving,
  saveButtonLabel = "Salvar serviços do canal",
  saveButtonDisabled = false,
  onToggleService,
  onSave,
}: ChannelCatalogPanelProps) {
  return (
    <div className="border border-dashed border-gray-300 rounded-lg p-4 space-y-4">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <h4 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
            <Layers3 size={16} />
            Serviços habilitados do Canal
          </h4>
          <p className="text-xs text-muted-foreground mt-0.5">
            Aqui fica o vínculo comercial do canal com os serviços da plataforma. Somente serviços habilitados nesta seção poderão ser contratados pelas empresas vinculadas a este canal.
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onSave}
          className="flex items-center gap-1 text-xs"
          disabled={isSaving || isLoading || saveButtonDisabled}
        >
          {isSaving ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />}
          {saveButtonLabel}
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <div className="rounded-lg bg-slate-50 p-3">
          <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">{channelContextLabel}</p>
          <p className="mt-1 text-sm font-semibold text-slate-900">{channelLabel}</p>
        </div>
        <div className="rounded-lg bg-slate-50 p-3">
          <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">Serviços habilitados</p>
          <p className="mt-1 text-2xl font-semibold text-slate-900">{servicesInCatalog}</p>
        </div>
        <div className="rounded-lg bg-slate-50 p-3">
          <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">Serviços já contratados</p>
          <p className="mt-1 text-2xl font-semibold text-slate-900">{servicesContracted}</p>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        {services.map((service) => {
          const checked = draftServiceIds.includes(service.id);
          return (
            <label
              key={service.id}
              className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition ${checked ? "border-emerald-300 bg-emerald-50" : "border-slate-200 bg-white hover:border-slate-300"}`}
            >
              <input
                type="checkbox"
                className="mt-1 h-4 w-4 rounded border-slate-300"
                checked={checked}
                onChange={(event) => onToggleService(service.id, event.target.checked)}
                disabled={isSaving}
                aria-label={`Habilitar serviço ${service.name}`}
              />
              <div className="space-y-2">
                <div>
                  <p className="text-sm font-semibold text-slate-900">{service.name}</p>
                  <p className="text-xs text-slate-500">{service.description || service.contractLabel || "Sem descrição operacional cadastrada."}</p>
                </div>
                <div className="flex flex-wrap gap-2 text-[11px] text-slate-500">
                  <span className="rounded-full bg-white px-2.5 py-1">{service.category || "Sem categoria"}</span>
                  <span className="rounded-full bg-white px-2.5 py-1">{service.modules?.length || 0} módulo(s)</span>
                  <span className="rounded-full bg-white px-2.5 py-1">{service.companyContracted ? "Já contratado por empresa(s)" : "Ainda não contratado"}</span>
                </div>
              </div>
            </label>
          );
        })}

        {!isLoading && services.length === 0 && (
          <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-500 md:col-span-2">
            Nenhum serviço ativo foi encontrado para o catálogo comercial deste canal.
          </div>
        )}

        {isLoading && (
          <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-500 md:col-span-2">
            Carregando serviços habilitados do canal...
          </div>
        )}
      </div>
    </div>
  );
}
